import React, { PropsWithChildren } from "react";
import { ParentIframeProvider } from "@trello-powerup-monorepo/iframe-messenger-v2";
import {
  PaywallRouteGuardProps,
  PaywallRouteGuard,
} from "./guards/paywallRouteGuard";
import { PaywallIframeManagerProvider } from "./context/paywalliframeManager.context";
import {
  PaywallStateManagerProvider,
  PaywallStateManagerProviderProps,
} from "./context/paywallStateManager.context";
import {
  PaywallGuardContent,
  PaywallGuardContentProps,
} from "./guards/components/PaywallGuardContent";
import { ParentPaywallProvider } from "./context/parentPaywall.context";

export interface ParentIframeProviderProps {
  baseUrl: string;
  trustedOrigins: string[];
  container?: string | HTMLElement;
  debug?: (message: string, ...args: any[]) => void;
}

export interface ParentPaywallProviderProps {
  appId: string;
  guardConfig: PaywallRouteGuardProps;
  guardContentConfig: PaywallGuardContentProps;
  iframeProviderConfig: ParentIframeProviderProps;
  stateManagerConfig: PaywallStateManagerProviderProps;
  // AIDEV-NOTE: Optional callback to expose generated sessionId to host app
  onSessionId?: (sessionId: string) => void;
}

export interface ParentPaywallProviderContextValue {
  showDashboard: () => void;
  showCreateProfile: () => void;
  showPricing: () => void;
  hide: () => void;
  // EXISTING - Events (unchanged for backward compatibility)
  onProfileCreated: (handler: () => void) => () => void;
}

export function ParentPaywallWrapper({
  children,
  appId,
  guardConfig,
  guardContentConfig,
  iframeProviderConfig,
  stateManagerConfig,
  onSessionId,
}: PropsWithChildren<ParentPaywallProviderProps>) {
  const defaultContainerId = "paywall-container";
  const iframeConfig = React.useMemo(
    () => ({
      baseUrl: iframeProviderConfig.baseUrl,
      trustedOrigins: iframeProviderConfig.trustedOrigins,
      container: iframeProviderConfig.container ?? `#${defaultContainerId}`,
      initialVisibility: "hidden" as const,
      debug: iframeProviderConfig.debug,
    }),
    [iframeProviderConfig]
  );

  return (
    <>
      {typeof iframeConfig.container === "string" ? (
        <div
          id={defaultContainerId}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1000,
            pointerEvents: "none",
          }}
        ></div>
      ) : (
        iframeConfig.container
      )}
      <ParentIframeProvider config={iframeConfig}>
        <PaywallIframeManagerProvider appId={appId} onSessionId={onSessionId}>
          <PaywallStateManagerProvider {...stateManagerConfig}>
            <PaywallRouteGuard {...guardConfig}>
              <PaywallGuardContent {...guardContentConfig}>
                <ParentPaywallProvider>{children}</ParentPaywallProvider>
              </PaywallGuardContent>
            </PaywallRouteGuard>
          </PaywallStateManagerProvider>
        </PaywallIframeManagerProvider>
      </ParentIframeProvider>
    </>
  );
}
